import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { NewsArticle, AdPosition } from "../types";
import { newsApi } from "../services/api";
import adService from "../services/adService";
import AdComponent from "../components/AdComponent";
import { LoadingSkeleton } from "../components/common";
import SEOMetaTags from "../components/SEOMetaTags";
import SocialShareButtons from "../components/SocialShareButtons";
import moment from "moment";
import { useErrorHandler } from "../hooks";
import "moment/locale/tr";

const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Reklamlar için state
  const [adPositions, setAdPositions] = useState<{
    [key: string]: AdPosition[];
  }>({
    ABOVE_TITLE: [],
    BELOW_TITLE: [],
    MIDDLE_CONTENT: [],
    AFTER_CONTENT: [],
    SIDEBAR: [],
  });
  const [adsLoading, setAdsLoading] = useState<boolean>(true);

  // Handle any errors with toast and redirect
  useErrorHandler(error, { redirectTo: "/404" });

  // Haberi yükle
  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) {
        setError("Haber bulunamadı");
        setLoading(false);
        return;
      }

      // Eğer slug 'undefined' string olarak geldiyse
      if (slug === "undefined") {
        setError("Haber bulunamadı");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Slug ile haberi getir (view count devre dışı)
        const data = await newsApi.getBySlug(slug, false);
        setArticle(data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Haber bulunamadı");
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  // Reklamları yükle
  useEffect(() => {
    const fetchAds = async () => {
      try {
        setAdsLoading(true);
        const screenSize = isMobile ? "MOBILE" : "DESKTOP";

        // Akıllı reklam pozisyonlama - gelir optimizasyonu için içerik ortası dahil edildi
        const positions = [
          "ABOVE_TITLE",
          "BELOW_TITLE", 
          "MIDDLE_CONTENT", // Yeniden aktifleştirildi - yüksek gelir potansiyeli
          "AFTER_CONTENT",
          "SIDEBAR",
        ];
        const adsData: { [key: string]: AdPosition[] } = {
          ABOVE_TITLE: [],
          BELOW_TITLE: [],
          MIDDLE_CONTENT: [], // Aktif hale getirildi
          AFTER_CONTENT: [],
          SIDEBAR: [],
        };

        // Kategori ve haber ID'lerini belirle
        const newsId = article?.id;
        const categoryId = article?.category?.id;

        console.log(
          "Reklam yükleme isteği - Haber ID:",
          newsId,
          "Kategori ID:",
          categoryId
        );

        // Her pozisyon için akıllı reklam sorgulama
        await Promise.all(
          positions.map(async (position) => {
            console.log(`${position} pozisyonu için reklam yükleniyor...`);
            // Akıllı reklam getirme metodunu kullan - haber ve kategori hedeflemeli reklamları önceliklendirir
            const positionAds = await adService.getSmartAds(
              position,
              screenSize,
              newsId,
              categoryId
            );
            console.log(`${position} reklamları:`, positionAds);
            adsData[position] = positionAds;
          })
        );

        console.log("Tüm reklamlar yüklendi:", adsData);
        setAdPositions(adsData);
      } catch (error) {
        console.error("Reklamlar yüklenirken hata oluştu:", error);
      } finally {
        setAdsLoading(false);
      }
    };

    // Haber yüklendiyse reklamları getir
    if (article) {
      fetchAds();
    }
  }, [article, isMobile]);

  if (loading) {
    return <LoadingSkeleton variant="article" />;
  }

  if (!article) {
    return null; // Error is already shown via toast, just return null
  }

  // Format date to a readable format
  moment.locale("tr");
  const formattedDate = moment(article.createdDate).format(
    "DD MMMM YYYY HH:mm"
  );
  const isUpdated = article.createdDate !== article.updatedDate;
  const formattedUpdateDate = isUpdated
    ? moment(article.updatedDate).format("DD MMMM YYYY HH:mm")
    : null;

  // İçeriği reklamlar ile bölelim (ortada gösterilecek reklamlar için)
  const renderContentWithAds = () => {
    if (!article || !adPositions.MIDDLE_CONTENT || adPositions.MIDDLE_CONTENT.length === 0) {
      return <div dangerouslySetInnerHTML={{ __html: article?.content || '' }} />;
    }

    // İçeriği paragraflara böl ve araya reklam ekle
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = article.content;
    const paragraphs = tempDiv.querySelectorAll('p');
    
    if (paragraphs.length < 4) {
      // Kısa içerik için reklam ekleme
      return <div dangerouslySetInnerHTML={{ __html: article.content }} />;
    }

    const elements: React.ReactElement[] = [];
    const midPoint = Math.floor(paragraphs.length / 2);
    
    // İlk yarıyı ekle
    const firstHalf = Array.from(paragraphs).slice(0, midPoint);
    if (firstHalf.length > 0) {
      elements.push(
        <div 
          key="first-half" 
          dangerouslySetInnerHTML={{ 
            __html: firstHalf.map(p => p.outerHTML).join('') 
          }} 
        />
      );
    }

    // Ortaya reklam ekle
    if (adPositions.MIDDLE_CONTENT.length > 0) {
      const randomAd = adPositions.MIDDLE_CONTENT[Math.floor(Math.random() * adPositions.MIDDLE_CONTENT.length)];
      elements.push(
        <AdComponent
          key="middle-ad"
          adCode={randomAd.adCode}
          clickUrl={randomAd.clickUrl}
          position="MIDDLE_CONTENT"
        />
      );
    }

    // İkinci yarıyı ekle
    const secondHalf = Array.from(paragraphs).slice(midPoint);
    if (secondHalf.length > 0) {
      elements.push(
        <div 
          key="second-half" 
          dangerouslySetInnerHTML={{ 
            __html: secondHalf.map(p => p.outerHTML).join('') 
          }} 
        />
      );
    }

    return elements;
  };

  return (
    <>
      {/* SEO Meta Tags */}
      {article && (
        <SEOMetaTags
          title={article.title}
          description={article.summary || article.title}
          keywords={`${article.category?.name}, haber, ${article.title
            .split(" ")
            .slice(0, 5)
            .join(", ")}`}
          author={"Akademik Kadro"}
          imageUrl={article.imageUrl?.startsWith('http') ? article.imageUrl : `${window.location.origin}${article.imageUrl}`}
          url={window.location.href}
          type="article"
          publishedTime={article.createdDate}
          modifiedTime={article.updatedDate}
          category={article.category?.name}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* Ana İçerik Alanı */}
        <Box
          sx={{
            flex: "1 1 auto",
            maxWidth: { xs: "100%", md: "calc(100% - 300px)" },
          }}
        >
          {/* Başlık üstü reklam alanı */}
          {adPositions.ABOVE_TITLE.map((ad, index) => (
            <AdComponent
              key={`above-title-${index}`}
              adCode={ad.adCode}
              clickUrl={ad.clickUrl}
              position="ABOVE_TITLE"
            />
          ))}



          {/* Article Title */}
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            {article.title}
          </Typography>

          {/* Başlık altı reklam alanı */}
          {adPositions.BELOW_TITLE.map((ad, index) => (
            <AdComponent
              key={`below-title-${index}`}
              adCode={ad.adCode}
              clickUrl={ad.clickUrl}
              position="BELOW_TITLE"
            />
          ))}



          {/* Article Summary */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              bgcolor: theme.palette.grey[50],
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontStyle: "italic", fontWeight: 500 }}
            >
              {article.summary}
            </Typography>
          </Paper>

          {/* Article Featured Image */}
          <Box sx={{ position: "relative", mb: 4 }}>
            <Box
              component="img"
              src={article.imageUrl || "/placeholder-news.jpg"}
              alt={article.title}
              loading="lazy"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 1,
                objectFit: "contain",
                display: "block",
                backgroundColor: theme.palette.grey[50],
              }}
            />
            
            {/* Updated info with entry and update dates - positioned at bottom right of image */}
            <Box
              sx={{
                mt: 1,
                textAlign: "right",
                fontSize: "0.85rem",
                color: theme.palette.text.secondary,
                fontStyle: "italic",
              }}
            >
              <div>Haber Giriş: {formattedDate}</div>
              <div>Son Güncelleme: {formattedUpdateDate || formattedDate}</div>
            </Box>
          </Box>

          {/* Article Content with Ads */}
          <Box
            sx={{
              mb: 4,
              typography: "body1",
              "& p": { mb: 2 },
              "& h2": { mt: 4, mb: 2, fontWeight: 600 },
              "& h3": { mt: 3, mb: 1.5, fontWeight: 600 },
              "& a": { color: "primary.main" },
              "& img": { my: 2, maxWidth: "100%", height: "auto" },
            }}
          >
            {/* Render content with ads in between */}
            {renderContentWithAds()}
          </Box>

          {/* Sosyal Medya Paylaşım Butonları */}
          <SocialShareButtons 
            article={article} 
            currentUrl={window.location.href} 
          />

          {/* İçerik sonrası reklam alanı */}
          {adPositions.AFTER_CONTENT.map((ad, index) => (
            <AdComponent
              key={`after-content-${index}`}
              adCode={ad.adCode}
              clickUrl={ad.clickUrl}
              position="AFTER_CONTENT"
            />
          ))}



          <Divider sx={{ mb: 4 }} />
        </Box>

        {/* Kenar Çubuğu (SIDEBAR) Reklam Alanı */}
        {adPositions.SIDEBAR && adPositions.SIDEBAR.length > 0 && (
          <Box
            sx={{
              width: { xs: "100%", md: "300px" },
              flex: "0 0 auto",
              position: "relative",
              mt: { xs: 0, md: 10 }, // Biraz aşağıdan başlasın
            }}
          >
            <Box
              sx={{
                position: { xs: "relative", md: "sticky" },
                top: "20px",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {adPositions.SIDEBAR.map((ad, index) => (
                <AdComponent
                  key={`sidebar-${index}`}
                  adCode={ad.adCode}
                  clickUrl={ad.clickUrl}
                  showBorders={true}
                  position="SIDEBAR"
                  containerStyles={{
                    margin: "0 auto",
                    maxWidth: "100%",
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ArticlePage;
